import { Box, Image, Typography, useTheme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import ValueDescriptionVideoBg from "../../../images/value-description-video-section-bg.svg";
import MuxVideoPlayer from "../MuxVideoPlayer";
import { useTopValues } from "./hooks/useTopValues";
import PageSectionTitle from "./PageSectionTitle";

const TextLabel = ({ text }: { text: string }) => {
  return (
    <Box
      sx={{
        borderRadius: "1rem",
        backgroundColor: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Box sx={{ padding: "1rem" }}>
        <PageSectionTitle title={text} sx={{ paddingBottom: "0px" }} />
      </Box>
    </Box>
  );
};

const PictureWithDescription = ({
  src,
  altText,
  text,
  title
}: {
  src: string;
  altText: string;
  text: string;
  title?: string;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        flex: "1",
        textAlign: "center",
        color: theme.palette.common.white
      }}
    >
      <Image
        sx={{
          width: "5rem",
          height: "5rem"
        }}
        src={src}
        alt={altText}
      />

      <Typography emphasized variant="h6" sx={{ paddingBottom: "1rem" }}>
        {title}
      </Typography>

      <Typography variant="body2">{text}</Typography>
    </Box>
  );
};

const WhyTutoringOnOutschool = React.forwardRef<HTMLElement>((_, ref) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { t } = useTranslation(
    "client\\components\\tutoring\\TutoringPage\\WhyTutoringOnOutschool"
  );
  const valueDescriptions = useTopValues();

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundImage: `url("${ValueDescriptionVideoBg}")`,
        backgroundSize: "45% auto",
        backgroundPosition: "top right",
        borderRadius: "3.2rem",
        backgroundRepeat: "no-repeat",
        backgroundColor: `${theme.palette.primary.dark}`
      }}
    >
      <Box
        sx={{
          padding: "3rem 3rem 0",
          display: "flex"
        }}
      >
        <TextLabel text={t("Say hello to fun, flexible tutoring")} />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: `${isMobile ? "column" : "row"}`,
          alignItems: "flex-start",
          padding: "3rem",
          justifyContent: `${isMobile ? "center" : "space-between"}`
        }}
      >
        {valueDescriptions.map(
          ({ image, alt, imageTitle, description }, index) => (
            <PictureWithDescription
              src={image}
              altText={alt}
              title={imageTitle}
              text={description}
              key={`${index}-${imageTitle}`}
            />
          )
        )}

        <Box sx={{ flex: "2" }}>
          <MuxVideoPlayer
            // eslint-disable-next-line i18next/no-literal-string
            playButtonType="white-center"
            // eslint-disable-next-line i18next/no-literal-string
            videoPlaybackID={"UTYVhdOVCY9bavBIDFk6Hm02UcaxaJtkJ"}
            thumbnailUrl={
              // eslint-disable-next-line i18next/no-literal-string
              "https://image.mux.com/iPWZ01J69A6HCR99IVXV3WYMJ1f00SDfmt/thumbnail.jpg?time=1&height=318&width=566"
            }
            sx={{
              borderRadius: "xlarge",
              // When the thumbnail is loading, the container has no height.
              // This makes the play button float in space at the top of the render area.
              // Hide overflow so this button is not visible during thumbnail load.
              overflow: "hidden"
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default WhyTutoringOnOutschool;
